import React, {useContext, useEffect, useState} from "react";
import {PageHeading} from "@ui-components/Container";
import {FormattedMessage, useIntl} from "react-intl";
import CountryContext from "../CountryContext";
import CountryConfigContext from "../CountryConfigContext";
import useCountryAxiosInstance from "@hooks/useCountryAxiosInstance";
import {NotificationsContext} from "@ui-components/Notifications";
import Button from "@ui-components/Button";
import {useHistory} from "react-router-dom";
import Toggle from "@ui-components/Toggle";
import {CardsContainer} from "@feature/tintometers/cards-container";
import {SurveyCreationModal} from "@feature/tintometers/survey-creation-modal";
import {Option} from "@feature/commons";
import {useTintometerSurveys} from "@feature/tintometers/hooks/useTintometerSurveys";
import {ServicesWithIdSchema} from "@feature/types";
import {Operator} from "@utils/pagination/types";
import Label from "@ui-components/Label";
import Input from "@ui-components/Input";
import SearchSelect from "@ui-components/SearchSelect";
import {ExclamationTriangleIcon, PlusCircleIcon, TrashIcon} from "@heroicons/react/24/outline";
import DatePicker from "@ui-components/DatePicker";
import {Color} from "@feature/tintometers/types";
import IconButton from "@ui-components/IconButton";
import moment from "moment";
import {TintometerServiceOption} from "@feature/tintometers/survey-creation-modal/types";


// Constants
const PAGE_SIZE: number = 4;
const CREATOR_SEARCH_MIN_LENGTH: number = 3;


const Tintometers = () => {

  // hooks
  const intl = useIntl();
  const history = useHistory();
  const countryApiRequest = useCountryAxiosInstance();

  // context
  const {country} = useContext(CountryContext);
  const {config: {services}} = useContext(CountryConfigContext);
  const {push} = useContext(NotificationsContext);

  // state
  // -- commons
  const [colors, setColors] = useState<Color[]>([]);
  const [newSurveyModalData, setNewSurveyModalData]
    = useState<{mode: "create" | "duplicate", data?: any}>();
  // -- filters
  const [tintometerSurveysFilters, setTintometerSurveysFilters] = useState<{
    name: string | undefined;
    services: Option[];
    dateRange: {startDate: Date | undefined; endDate: Date | undefined};
    includeClosed: boolean;
  }>({name: undefined, services: [], dateRange: {startDate: undefined, endDate: undefined}, includeClosed: false});
  const [worksiteSurveysFilters, setWorksiteSurveysFilters] = useState<{
    name: string | undefined;
    services: Option[];
    dateRange: {startDate: Date | undefined; endDate: Date | undefined};
    worksite_name: string | undefined;
    creator: Option | undefined;
  }>({name: undefined, worksite_name: undefined, services: [], creator: undefined, dateRange: {startDate: undefined, endDate: undefined}});
  // -- creators search
  const [createdBySearchValue, setCreatedBySearchValue] = useState("");
  const [showCreatedByFilterWarning, setShowCreatedByFilterWarning] = useState(false)

  const {
    loading: loadingTintometerSurveys,
    submitting: submittingNewSurvey,
    data: tintometerSurveysData,
    page: tintometerSurveysPage,
    setPage: setTintometerSurveysPage,
    create: createTintometerSurvey,
    delete_: deleteTintometerSurvey,
    refetch: refetchTintometerSurveys,
  } = useTintometerSurveys(
    "private",
    [
      ...(tintometerSurveysFilters.name ? [{
        column: "survey_name",
        operator: "ilike" as Operator,
        value: tintometerSurveysFilters.name,
      }] : []),
      ...(tintometerSurveysFilters.services.length > 0 ? [{
        column: "survey_service_id",
        operator: "in" as Operator,
        value: tintometerSurveysFilters.services.map((s) => s.value),
      }] : []),
      ...(!!tintometerSurveysFilters.dateRange?.startDate ? [{
        column: "start_date",
        operator: ">=" as Operator,
        value: moment(tintometerSurveysFilters.dateRange.startDate).format("YYYY-MM-DD"),
      }] : []),
      ...(!!tintometerSurveysFilters.dateRange?.endDate ? [{
        column: "end_date",
        operator: "<=" as Operator,
        value: moment(tintometerSurveysFilters.dateRange.endDate).format("YYYY-MM-DD"),
      }] : []),
    ],
    PAGE_SIZE,
    tintometerSurveysFilters.includeClosed,
  );
  const {
    loading: loadingWorksiteSurveys,
    data: worksiteSurveysData,
    page: worksiteSurveysPage,
    setPage: setWorksiteSurveysPage,
    duplicate: duplicateWorksiteSurvey,
    submitting: submittingDuplicateWorksiteSurvey,
  } = useTintometerSurveys(
    "worksites",
    [
      ...(worksiteSurveysFilters.name ? [{
        column: "survey_name",
        operator: "ilike" as Operator,
        value: `%${worksiteSurveysFilters.name}%`,
      }] : []),
      ...(worksiteSurveysFilters.worksite_name ? [{
        column: "worksite_name",
        operator: "ilike" as Operator,
        value: `%${worksiteSurveysFilters.worksite_name}%`,
      }] : []),
      ...(worksiteSurveysFilters.services.length > 0 ? [{
        column: "survey_service_id",
        operator: "in" as Operator,
        value: worksiteSurveysFilters.services.map((s) => s.value),
      }] : []),
      ...(!!worksiteSurveysFilters.dateRange?.startDate ? [{
        column: "start_date",
        operator: ">=" as Operator,
        value: moment(worksiteSurveysFilters.dateRange.startDate).format("YYYY-MM-DD"),
      }] : []),
      ...(!!worksiteSurveysFilters.dateRange?.endDate ? [{
        column: "end_date",
        operator: "<=" as Operator,
        value: moment(worksiteSurveysFilters.dateRange.endDate).format("YYYY-MM-DD"),
      }] : []),
      ...(!!worksiteSurveysFilters.creator ? [{
        column: "created_by",
        operator: "in" as Operator,
        value: worksiteSurveysFilters.creator.value,
      }] : []),
    ],
    PAGE_SIZE,
  );

  useEffect(() => {
    // On page load, fetch colors
    countryApiRequest!.get("/tintometers/colors/all")
      .then((res: any) => setColors(res));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return <>
    <div className="pt-4 pb-12 flex flex-col gap-12">
      <PageHeading
        title={intl.formatMessage({id: "tintometers"})}
        contentActions={undefined}
      />
      {/* ------------------------- TINTOMETRIST SURVEYS ------------------------- */}
      <div className="flex flex-col gap-4 -mt-8">
        <div className="flex flex-col gap-y-2 md:flex-row md:justify-between md:items-center">
          <h1 className="text-2xl text-am-700">
            <FormattedMessage
              id="KCC_tintometer_configurations_alt"
              defaultMessage="Configurazioni tintometriche"
            />
          </h1>
          <div className="flex flex-row gap-6 items-center">
            {/* @ts-ignore */}
            <Toggle
              label={<FormattedMessage id="include_closed_fp" defaultMessage="Includi chiusi" />}
              checked={tintometerSurveysFilters.includeClosed}
              onChange={() => setTintometerSurveysFilters((prev) => ({
                ...prev,
                includeClosed: !prev.includeClosed
              }))}
              marginCls=""
            />
            <Button
              onClick={() => setNewSurveyModalData({mode: "create"})}
            >
              <PlusCircleIcon className="w-4" />
              <FormattedMessage id="add" defaultMessage="Aggiungi" />
            </Button>
          </div>
        </div>
        <CardsContainer
          loading={loadingTintometerSurveys}
          surveys={tintometerSurveysData?.items ?? []}
          colors={colors}
          totalSurveys={tintometerSurveysData?.total ?? 0}
          pageSize={PAGE_SIZE}
          page={tintometerSurveysPage}
          setPage={setTintometerSurveysPage}
          filters={
            <div className="flex flex-col md:flex-row gap-x-8 gap-y-2 md:items-center md:justify-between">
              <div className="flex flex-col md:flex-row gap-x-4 gap-y-2 md:items-center">
                <div className="flex flex-row gap-2 items-center">
                  <Label disabled={false}>
                    <FormattedMessage id="name" defaultMessage="Nome"/>
                  </Label>
                  {/* @ts-ignore */}
                  <Input
                    type="text"
                    value={tintometerSurveysFilters.name || ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTintometerSurveysFilters((prev) => ({
                      ...prev,
                      name: e.target.value || undefined,
                    }))}
                    hideBottomMargin
                  />
                </div>
                <div className="flex flex-row gap-2 items-center">
                  <Label disabled={false}>
                    <FormattedMessage id="configurators" defaultMessage="Configuratori"/>
                  </Label>
                  {/* @ts-ignore */}
                  <SearchSelect
                    options={services
                      .filter((s: ServicesWithIdSchema) => s.type === "tintometer")
                      .map((s: ServicesWithIdSchema) => ({label: s.name, value: s.id}))}
                    value={tintometerSurveysFilters.services}
                    onChange={(selected: Option[]) => setTintometerSurveysFilters((prev) => ({
                      ...prev,
                      services: selected,
                    }))}
                    isMulti
                    isClearable
                    mainStyle="mt-1 border focus:ring-am-500 focus:border-black-500 block w-full text-black-500 sm:text-sm border-gray-300 disabled:opacity-50"
                  />
                </div>
              </div>
              <div className="flex flex-row gap-4 items-center">
                <div className="flex flex-row gap-2 items-center">
                  <Label disabled={false}>
                    <span className="whitespace-nowrap">
                      <FormattedMessage id="created_from_ms" defaultMessage="Creato dal"/>
                    </span>
                  </Label>
                  {/* @ts-ignore */}
                  <DatePicker
                    value={tintometerSurveysFilters.dateRange?.startDate || null}
                    onChange={(date: Date | null) => setTintometerSurveysFilters((prev) => ({
                      ...prev,
                      dateRange: {
                        ...prev.dateRange,
                        startDate: date || undefined,
                      },
                    }))}
                    minDate={null}
                  />
                  <Label disabled={false}>
                    <FormattedMessage id="to_ms" defaultMessage="al"/>
                  </Label>
                  {/* @ts-ignore */}
                  <DatePicker
                    value={tintometerSurveysFilters.dateRange?.endDate || null}
                    onChange={(date: Date | null) => setTintometerSurveysFilters((prev) => ({
                      ...prev,
                      dateRange: {
                        ...prev.dateRange,
                        endDate: date || undefined,
                      },
                    }))}
                    minDate={null}
                  />
                </div>
              </div>
            </div>
          }
          cardActions={(survey) => (
            <div className="w-full flex flex-row gap-2 items-center justify-between">
              <div>
                {survey.closed && (
                  <span className="text-red-500 uppercase">
                    <FormattedMessage id="closedF" defaultMessage="Chiusa"/>
                  </span>
                )}
              </div>
              <div className="flex flex-row gap-2">
                <Button onClick={() => {
                  history.push(`/${country.code}/service/${survey.service_id}/survey/${survey.id}`);
                }}>
                  <FormattedMessage id="open_cta" defaultMessage="Apri "/>
                </Button>
                <IconButton
                  styleType="empty"
                  Icon={TrashIcon}
                  onClick={async () => {
                    if (!confirm(intl.formatMessage({
                      id: "confirm_delete_survey",
                      defaultMessage: "Sei sicuro di volere eliminare la configurazione selezionata?"
                    })))
                      return;
                    await deleteTintometerSurvey(
                      survey.id,
                      () => {
                        push({
                          type: "success",
                          title: intl.formatMessage({
                            id: "survey_deleted_success",
                            defaultMessage: "Configurazione eliminata con successo"
                          }),
                        });
                        refetchTintometerSurveys();
                      },
                    )
                  }}
                />
              </div>
            </div>
          )}
          hideAuthor
        />
      </div>
      {/* ------------------------- WORKSITE SURVEYS ------------------------- */}
      <div className="flex flex-col gap-4">
        <h1 className="text-2xl text-am-700">
          <FormattedMessage
            id="KCC_worksite_configurations_alt"
            defaultMessage="Configurazioni da cantieri"
          />
        </h1>
        <div>
          <CardsContainer
            loading={loadingWorksiteSurveys}
            surveys={worksiteSurveysData?.items ?? []}
            colors={colors}
            totalSurveys={worksiteSurveysData?.total ?? 0}
            pageSize={PAGE_SIZE}
            page={worksiteSurveysPage}
            setPage={setWorksiteSurveysPage}
            filters={
              <div className="flex flex-col gap-2">
                <div className="flex flex-col lg:flex-row gap-x-4 gap-y-2 lg:items-center">
                  <div className="flex flex-col md:flex-row gap-x-4 gap-y-2 md:items-center">
                    <div className="flex flex-row gap-2 items-center">
                      <Label disabled={false}>
                        <FormattedMessage id="name" defaultMessage="Nome"/>
                      </Label>
                      {/* @ts-ignore */}
                      <Input
                        type="text"
                        value={worksiteSurveysFilters.name || ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWorksiteSurveysFilters((prev) => ({
                          ...prev,
                          name: e.target.value || undefined,
                        }))}
                        hideBottomMargin
                      />
                    </div>
                    <div className="flex flex-row gap-2 items-center">
                      <Label disabled={false}>
                        <span className="whitespace-nowrap">
                          <FormattedMessage id="worksite_name" defaultMessage="Nome cantiere"/>
                        </span>
                      </Label>
                      {/* @ts-ignore */}
                      <Input
                        type="text"
                        value={worksiteSurveysFilters.worksite_name || ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWorksiteSurveysFilters((prev) => ({
                          ...prev,
                          worksite_name: e.target.value || undefined,
                        }))}
                        hideBottomMargin
                      />
                    </div>
                  </div>
                  <div className="flex flex-col md:flex-row gap-x-4 gap-y-2 md:items-center">
                    <div className="flex flex-row gap-2 items-center">
                      <Label disabled={false}>
                        <FormattedMessage id="configurators" defaultMessage="Configuratori"/>
                      </Label>
                      {/* @ts-ignore */}
                      <SearchSelect
                        options={services
                          .filter((s: ServicesWithIdSchema) => s.type === "configurator" && s.class === 1)
                          .map((s: ServicesWithIdSchema) => ({label: s.name, value: s.id}))}
                        value={worksiteSurveysFilters.services}
                        onChange={(selected: Option[]) => setWorksiteSurveysFilters((prev) => ({
                          ...prev,
                          services: selected,
                        }))}
                        isMulti
                        isClearable
                        mainStyle="mt-1 border focus:ring-am-500 focus:border-black-500 block w-full text-black-500 sm:text-sm border-gray-300 disabled:opacity-50"
                      />
                    </div>
                    <div className="flex flex-row gap-2 items-center">
                      <div className={"flex flex-col"}>
                        <Label disabled={false}>
                          <span className="whitespace-nowrap">
                            <FormattedMessage id={"created_by"} defaultMessage={"Creato da"}/>
                          </span>
                        </Label>
                        {showCreatedByFilterWarning &&
                          <div className={"flex flex-row items-center gap-1"}>
                            <ExclamationTriangleIcon className={"w-3 text-yellow-700"}/>
                            <span className={"text-xs text-yellow-700 whitespace-nowrap"}>
                              <FormattedMessage id={"created_by_warning"} defaultMessage={"Minimo 3 caratteri"}/>
                            </span>
                          </div>
                        }
                      </div>
                      {/* @ts-ignore */}
                      <SearchSelect
                        loadOptions={async (search: string, callback: (options: Option[]) => void) => {
                          // On search change, set the search value
                          setCreatedBySearchValue(search);
                          if (search.length < CREATOR_SEARCH_MIN_LENGTH) {
                            // If search value has less than 3 characters, clear the filter and show the warning
                            setWorksiteSurveysFilters((prev => ({
                              ...prev,
                              creator: undefined,
                            })));
                            setShowCreatedByFilterWarning(true);
                          } else {
                            // Otherwise, fetch the creators and set the options. Also, hide the warning
                            const options = await countryApiRequest!.get(
                              `/service_surveys/retrieve_user_surveys_creators/${search}`
                            ) as { id: string; label: string }[];
                            setShowCreatedByFilterWarning(false);
                            callback(options.map((o) => ({
                              label: o.label,
                              value: o.id,
                            })));
                          }
                        }}
                        value={worksiteSurveysFilters.creator}
                        onChange={(value: Option | null) => {
                          setWorksiteSurveysFilters((prev) => ({
                            ...prev,
                            creator: value || undefined,
                          }));
                        }}
                        onFocus={() => {
                          // If user is focusing and there is a search value with less than 3 characters, show the warning
                          if (createdBySearchValue.length < CREATOR_SEARCH_MIN_LENGTH)
                            setShowCreatedByFilterWarning(true);
                        }}
                        onBlur={() => {
                          // On blur, hide the warning
                          setShowCreatedByFilterWarning(false);
                        }}
                        isClearable
                        mainStyle="mt-1 border focus:ring-am-500 focus:border-black-500 block w-full text-black-500 sm:text-sm border-gray-300 disabled:opacity-50"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex flex-row gap-4 items-center">
                  <div className="flex flex-row gap-2 items-center">
                    <Label disabled={false}>
                      <span className="whitespace-nowrap">
                        <FormattedMessage id="created_from_ms" defaultMessage="Creato dal"/>
                      </span>
                    </Label>
                    {/* @ts-ignore */}
                    <DatePicker
                      value={worksiteSurveysFilters.dateRange?.startDate || null}
                      onChange={(date: Date | null) => setWorksiteSurveysFilters((prev) => ({
                        ...prev,
                        dateRange: {
                          ...prev.dateRange,
                          startDate: date || undefined,
                        },
                      }))}
                      minDate={null}
                    />
                    <Label disabled={false}>
                      <FormattedMessage id="to_ms" defaultMessage="al"/>
                    </Label>
                    {/* @ts-ignore */}
                    <DatePicker
                      value={worksiteSurveysFilters.dateRange?.endDate || null}
                      onChange={(date: Date | null) => setWorksiteSurveysFilters((prev) => ({
                        ...prev,
                        dateRange: {
                          ...prev.dateRange,
                          endDate: date || undefined,
                        },
                      }))}
                      minDate={null}
                    />
                  </div>
                </div>
              </div>
            }
            cardActions={(survey) => (
              <div className="flex flex-row gap-2">
                <Button
                  onClick={async () => {
                    await duplicateWorksiteSurvey(
                      survey as any,
                      (s) => {
                        // On duplication success, redirect to the new survey page and show a success notification
                        refetchTintometerSurveys();
                        push({type: "success", title: intl.formatMessage({
                          id: "survey_duplicated_success",
                          defaultMessage: "Configurazione duplicata con successo",
                        })});
                      },
                      (err) => {
                        push({
                          type: "error",
                          title: intl.formatMessage({
                            id: "survey_duplicated_error",
                            defaultMessage: "Errore durante la duplicazione della configurazione",
                          }),
                          text: err.response?.status === 406
                            ? intl.formatMessage({
                              id: "survey_duplicated_error_not_compatible_services",
                              defaultMessage: "La configurazione selezionata non è compatibile con il configuratore scelto",
                            })
                            : undefined,
                        });
                        console.error(err);
                      },
                    );
                  }}
                  disabled={submittingDuplicateWorksiteSurvey}
                >
                  <FormattedMessage id="duplicate" defaultMessage="Duplica"/>
                </Button>
                <Button onClick={() => {
                  history.push(`/${country.code}/service/${survey.service_id}/survey/${survey.id}`);
                }}>
                  <FormattedMessage id="open_cta" defaultMessage="Apri"/>
                </Button>
              </div>
            )}
          />
        </div>
      </div>
    </div>
    {newSurveyModalData && (
      <SurveyCreationModal
        onConfirm={async (service: TintometerServiceOption) => {

          const serviceChosen = services?.find((s: ServicesWithIdSchema) => s.id === service.id);
          if (!serviceChosen)
            throw new Error("Cannot find chosen service");

          if (newSurveyModalData.mode === "create") {
            await createTintometerSurvey(
              serviceChosen,
              (s) => {
                // On creation success, redirect to the new survey page and show a success notification
                history.push(`/${country.code}/service/${s.service_id}/survey/${s.id}`);
                push({type: "success", title: intl.formatMessage({id: "survey_created_success"})});
              },
            );
          } else {
            throw new Error("Invalid survey modal mode");
          }
        }}
        onExit={() => setNewSurveyModalData(undefined)}
        options={services
          .filter((s: ServicesWithIdSchema) => s.type === "tintometer")
          .map((s: ServicesWithIdSchema) => ({title: s.name, description: s.description, id: s.id}))
        }
        loading={submittingNewSurvey}
        submitting={submittingDuplicateWorksiteSurvey}
      />
    )}
  </>
};

export default Tintometers;
